import React, {useState, useEffect, useCallback, useRef} from "react";

// CLASSES
import Settings from "@classes/Settings";

// HELPERS
import api from "@helpers/api";
import i18n from "@helpers/i18n";
import {getCookie} from "@helpers/cookies";
import {createB64Context, stringifyQueryParams, formatSSN} from "@helpers/utils";

// COMPONENTS
import Modal from "@components/Modal/Modal";
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator";
import ContentBox from "@components/ContentBox/ContentBox";

import FuturTable from "./FuturTable";

const FuturApplicationsView = (props) => {
	const {
		isCustomerModal,
		user,
		advisor,
		partner,
		showHeader,
		onWorkflowSwitched,
		caseFilterContext,
		setCustomerState,
		isAdviceSession,
		nextStepOptions,
		client
	} = props;

	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingCases, setIsLoadingCases] = useState(false);
	const [currentAction, setCurrentAction] = useState('none');
	const [isDisabled] = useState(false);
	const [isReminderModalVisible, setIsReminderModalVisible] = useState(false);
	const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
	const [userCaseList, setUserCaseList] = useState([]);
	const [totalCases, setTotalCases] = useState(0);
	const [error, setError] = useState(false);

	// Helper function to get cases
	const getUserFuturCaseList = useCallback((page, searchString, order, orderBy) => {
		const userSsn = user.ssn;
		const advisorSsn = advisor.ssn;
		const customerSsn = client?.id ? // if a client was passed as a prop (customer modal)
			formatSSN(client.id, {withDash: false})
			: client?.id === "" ? // handle client was passed as a prop but with no ssn set
				""
				: null; // no ssn passed means it's a main dashboard widget not filtered by customer

		// check if we have any options saved..
		const cobOptions = JSON.parse(getCookie("cobOptions")) || null;

		// .. and set rowsPerPage
		const rowsPerPage = cobOptions?.rowsPerPage["futur"] || 10;

		return new Promise(async (resolve, reject) => {
			try
			{
				console.log("Loading list of Futur applications...");
				const cases = await api("/futur/list", {
					userSsn,
					advisorSsn,
					customerSsn,
					filter: caseFilterContext.caseFilter.futur,
					page: page || 0,
					order: order || "desc",
					orderBy: orderBy || "created",
					searchString: searchString || "",
					limit: rowsPerPage === -1 ? 100 : rowsPerPage
				});

				resolve(cases);
			} catch (e)
			{
				reject(e);
			}
		});
	}, [user.ssn, advisor.ssn, client?.id, caseFilterContext.caseFilter.futur]);


	const isFirstRender = useRef(true);

	// Consolidated Effect for Data Fetching
	useEffect(() => {
		let isMounted = true;

		const fetchData = async () => {
			if (isFirstRender.current)
			{
				setIsLoading(true);
			}
			else
			{
				setIsLoadingCases(true);
			}

			try
			{
				const result = await getUserFuturCaseList();
				if (isMounted && result)
				{
					setIsLoading(false);
					setIsLoadingCases(false);
					setTotalCases(parseInt(result.total));
					setUserCaseList(result.batch);
				}
			} catch (err)
			{
				if (isMounted)
				{
					console.error(err);
					setError(true);
					setIsLoading(false);
					setIsLoadingCases(false);
				}
			}
		};

		fetchData();
		isFirstRender.current = false;

		return () => {
			isMounted = false;
		};
	}, [getUserFuturCaseList]);


	const continueConsultation = (caseId, partner, ssn, user) => {
		if (isCustomerModal)
		{
			document.documentElement.style.overflow = "auto"; // re-enable main page scroll
		}

		if (partner.futurcase)
		{
			const encodedContext = createB64Context(partner.partnerId + "client", user, ssn, caseId);
			window.location.href = partner.futurcase + stringifyQueryParams({
				context: encodedContext,
				token: Settings.get("AUTH_TOKEN"),
				key: "futur"
			});
		}
	};

	const downloadFile = (filename, base64pdf) => {
		// Create a link pointing to the ObjectURL containing the blob.
		const link = document.createElement('a');
		link.href = "data:application/pdf;base64," + base64pdf;
		link.download = filename;
		link.click();
	};


	const handleRowAction = async (action, caseId, ssn, callbackFunc) => {
		setCurrentAction(action);

		if (action === 'delete')
		{
			setIsLoadingCases(true);

			api("/case/delete", {
				caseId: caseId, userId: user.ssn, partnerId: advisor.ssn
			});

			const updatedList = userCaseList.filter(userCase => userCase.caseId !== caseId);
			setIsLoadingCases(false);
			setCurrentAction('none');
			setIsDeleteModalVisible(true);
			setUserCaseList(updatedList);

			return;
		}

		if (action === 'remind')
		{
			api("/futur/reminder", {
				caseId: caseId
			});

			setCurrentAction('none');
			setIsReminderModalVisible(true);

			return;
		}

		if (action === 'remind_meeting')
		{
			api("/case/reminder", {
				caseId: caseId
			});

			setCurrentAction('none');
			setIsReminderModalVisible(true);

			return;
		}

		if (action === 'download_meeting')
		{
			const rawDataInBase64 = await api("/case/downloadReceipt", {
				caseId: caseId
			}, {}, true);

			if (rawDataInBase64 !== "")
			{
				downloadFile(caseId + ".pdf", rawDataInBase64);
			}

			setCurrentAction('none');

			return;
		}

		if (action === 'download_futur')
		{
			const rawDataInBase64 = await api("/futur/download", {
				caseId: caseId, userId: user.ssn, partnerId: advisor.ssn
			}, {}, true);

			if (rawDataInBase64 !== "")
			{
				downloadFile(caseId + ".pdf", rawDataInBase64);
			}

			setCurrentAction('none');

			return;
		}

		if (action === 'copy')
		{
			const createdCaseId = await api("/case/copy", {
				caseId: caseId, userId: user.ssn, partnerId: advisor.ssn
			}, {}, true);

			continueConsultation(createdCaseId.result, partner, ssn, user);
		}

		if (action === 'continue')
		{
			continueConsultation(caseId, partner, ssn, user);
		}
	};

	const handleHideReminderModal = () => {
		setIsReminderModalVisible(false);
	};

	const handleHideDeleteModal = () => {
		setIsDeleteModalVisible(false);
	};


	return (
		<>
			<ContentBox style={
				isCustomerModal ?
					{
						minHeight: "66vh",
						margin: 0,
						padding: 0,
						overflow: "auto",
						boxShadow: "none"
					}
					:
					{
						width: "98.5%",
						margin: 0,
						marginBottom: "22.5px"
					}
			}>
				{showHeader && (
					<h2>
						{i18n("dashboard_selector", "futur")}
					</h2>
				)}
				{isLoading && (
					<div style={{padding: "91px 0"}}>
						<ActivityIndicator color="#4cb7d5" busy/>
					</div>
				)}
				{error && (
					<div style={{padding: "91px 0"}}>Ett fel uppstod när data laddades, kontakta supporten.</div>
				)}
				{!error && !isLoading && (
					<>
						<div className="dashboard-cases"
							 style={
								 isCustomerModal ?
									 {
										 minHeight: "unset"
									 }
									 :
									 {}
							 }>
							<FuturTable
								user={user}
								type="futur"
								partner={partner}
								advisor={advisor}
								data={userCaseList}
								totalCases={totalCases}
								isLoadingCases={isLoadingCases || false}
								currentAction={currentAction}
								whitelabel={partner.name}
								isCustomerModal={isCustomerModal || false}
								caseFilterContext={caseFilterContext}
								setCustomerState={setCustomerState}
								isAdviceSession={isAdviceSession || false}
								nextStepOptions={nextStepOptions}
								onWorkflowSwitched={onWorkflowSwitched}
								onAction={handleRowAction}
								getCases={getUserFuturCaseList}
							/>
							{isDisabled && (
								<div className="dashboard-cases-disabled">
									<ActivityIndicator color="#4cb7d5" busy/>
								</div>
							)}
						</div>
					</>
				)}
			</ContentBox>

			<Modal visible={isReminderModalVisible}>
				<p>En påminnelse om signering har skickats till kunden</p>
				<Modal.ButtonsContainer>
					<Modal.Button label="Ok" appearance="primary" onClick={handleHideReminderModal}/>
				</Modal.ButtonsContainer>
			</Modal>

			<Modal visible={isDeleteModalVisible}>
				<p>{i18n("dashboard_sessions", "draft_removed")}</p>
				<Modal.ButtonsContainer>
					<Modal.Button label="Ok" appearance="primary" onClick={handleHideDeleteModal}/>
				</Modal.ButtonsContainer>
			</Modal>
		</>
	);
};

export default FuturApplicationsView;
